import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@chapche/ui';
import PageTitleAsBreadCrumbs from '../../shared/PageTitleAsBreadCrumbs';
import {
    cookingStepSaveService,
    CookingStepSaveState,
} from '../../services/cookingStepSaveService';
import { popupOpenService } from '../../services/popupOpenService';
import { POPUP_KEY_CREATE_COOKING_STEP } from '../../constant';
import { CookingStep } from '../../services/recipeTypes';
import CookingStepEditor from './CookingStepEditor';
import CookingStepsList from './CookingStepsList';
import CreateCookingStepPopup from './CreateCookingStepPopup';
import DeleteCookingStepPopup from './DeleteCookingStepPopup';

const formatSavedAt = (date: Date | null, locale: string): string => {
    if (!date) {
        return '';
    }
    return date.toLocaleTimeString(locale === 'ru' ? 'ru-RU' : 'en-GB', {
        hour: '2-digit',
        minute: '2-digit',
    });
};

const CookingStepEditTab: React.FC = () => {
    const { i18n } = useTranslation();
    const { id: recipeType } = useParams<{ id: string }>();
    const [steps, setSteps] = useState<CookingStep[]>([]);
    const [selectedStepId, setSelectedStepId] = useState<string | null>(null);
    const [saveState, setSaveState] = useState<CookingStepSaveState>(
        cookingStepSaveService.getSaveState(),
    );

    useEffect(() => {
        const flushOnLeave = () => {
            cookingStepSaveService.flushPending();
        };
        window.addEventListener('beforeunload', flushOnLeave);
        return () => window.removeEventListener('beforeunload', flushOnLeave);
    }, []);

    useEffect(() => {
        if (!recipeType) {
            return undefined;
        }
        setSelectedStepId(null);
        const update = (nextSteps: CookingStep[]) => {
            const list = [...(nextSteps || [])];
            setSteps(list);
            setSelectedStepId((current) => {
                if (current && list.some((step) => step.id === current)) {
                    return current;
                }
                return list[0]?.id || null;
            });
        };
        cookingStepSaveService.loadSteps(recipeType, update);
        cookingStepSaveService.registerObserver(update);
        cookingStepSaveService.registerSaveStateObserver(setSaveState);
        return () => {
            cookingStepSaveService.flushPending();
            cookingStepSaveService.unRegisterObserver(update);
            cookingStepSaveService.unRegisterSaveStateObserver(setSaveState);
        };
    }, [recipeType]);

    const selectedStep = useMemo(
        () => steps.find((step) => step.id === selectedStepId) || null,
        [steps, selectedStepId],
    );

    const saveStatusLabel = (() => {
        if (saveState.status === 'saving') {
            return i18n.t('app.page.cookingStep.saveStatus.saving');
        }
        if (saveState.status === 'error') {
            return i18n.t('app.page.cookingStep.saveStatus.error');
        }
        if (saveState.status === 'saved') {
            const time = formatSavedAt(saveState.lastSavedAt, i18n.resolvedLanguage || 'ru');
            return time
                ? i18n.t('app.page.cookingStep.saveStatus.savedAt', { time })
                : i18n.t('app.page.cookingStep.saveStatus.saved');
        }
        return '';
    })();

    return (
        <div className="cooking-steps">
            <PageTitleAsBreadCrumbs />
            <div className="cooking-steps__page-header">
                <h1 className="recipe__title block__title-h2 cooking-steps__title">
                    {i18n.t(`recipe.item.type.${recipeType}`)}
                    <span className="cooking-steps__title-suffix">
                        {i18n.t('app.page.cookingStep.title')}
                    </span>
                </h1>
                <div className="cooking-steps__header-actions">
                    {saveStatusLabel ? (
                        <div className={`cooking-steps__save-status cooking-steps__save-status_${saveState.status}`}>
                            {saveState.status === 'saved' || saveState.status === 'saving' ? (
                                <span className="cooking-steps__save-dot" aria-hidden="true" />
                            ) : null}
                            <span>{saveStatusLabel}</span>
                            {saveState.status === 'error' ? (
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="small"
                                    onClick={() => cookingStepSaveService.retryLastFailed()}
                                >
                                    {i18n.t('app.page.cookingStep.saveStatus.retry')}
                                </Button>
                            ) : null}
                        </div>
                    ) : null}
                    {steps.length > 0 ? (
                        <Button onClick={() => popupOpenService.callToOpen(POPUP_KEY_CREATE_COOKING_STEP)}>
                            {i18n.t('app.page.cookingStep.addStep')}
                        </Button>
                    ) : null}
                </div>
            </div>

            {steps.length === 0 ? (
                <div className="cooking-steps__empty">
                    <p className="block__content">
                        {i18n.t('app.page.cookingStep.emptyForType', {
                            type: i18n.t(`recipe.item.type.${recipeType}`),
                        })}
                    </p>
                    <Button onClick={() => popupOpenService.callToOpen(POPUP_KEY_CREATE_COOKING_STEP)}>
                        {i18n.t('app.page.cookingStep.addFirstStep')}
                    </Button>
                </div>
            ) : (
                <div className="cooking-steps__split">
                    <CookingStepsList
                        steps={steps}
                        selectedStepId={selectedStepId}
                        onSelect={(stepId) => {
                            cookingStepSaveService.flushPending();
                            setSelectedStepId(stepId);
                        }}
                    />
                    <section className="cooking-steps__detail">
                        {selectedStep ? (
                            <CookingStepEditor step={selectedStep} recipeType={recipeType} />
                        ) : (
                            <p className="block__content cooking-steps__detail-empty">
                                {i18n.t('app.page.cookingStep.selectStep')}
                            </p>
                        )}
                    </section>
                </div>
            )}

            <CreateCookingStepPopup
                onCreated={(step) => {
                    if (step.id) {
                        setSelectedStepId(step.id);
                    }
                }}
            />
            <DeleteCookingStepPopup
                onDeleted={(deletedId, nextSelectedId) => {
                    setSelectedStepId((current) => (
                        current === deletedId ? nextSelectedId : current
                    ));
                }}
            />
        </div>
    );
};

export default CookingStepEditTab;
